import api from '@/lib/api/axios'

export interface ServiceNowConfig {
    id?: number
    instanceUrl: string
    username: string
    apiKey: string
    tableName: string
    customFieldVaultSentinel: string
    customFieldMvc: string
    enabled: boolean
    syncInterval: number
    syncIntervalUnit: 'minutes' | 'hours' | 'days'
    /** 'automatic': Vault Sentinel writes its ObjectID to the CI. 'manual': the customer's change team does. */
    writeBackMode?: 'automatic' | 'manual'
    lastSyncAt: string | null
    // Incident defaults (ServiceNow sys_ids) configured once in settings
    defaultCallerId?: string
    defaultAssignmentGroup?: string
}

export interface CMDBSyncItem {
    id: number
    sysId: string
    ciName: string | null
    ciClass: string | null
    ciDescription: string | null
    ciCategory: string | null
    ciOperationalStatus: string | null
    ciLocation: string | null
    vaultSentinelObjectId: string | null
    isMvc: boolean
    syncStatus: 'new' | 'changed' | 'deleted' | 'synced' | 'ignored' | 'removed'
    changeDetails: string | null
    linkedObjectId: number | null
    /** Linked to a Vault Sentinel object. Add, Apply and Remove all need this. */
    matched: boolean
    matchedObjectId: string | null
    /** Has been in Vault Sentinel through this integration, so Remove has something to take out. */
    wasAdded: boolean
    /** An unclaimed object whose ObjectID is the CI's name. A suggestion, never applied by itself. */
    suggestedObjectId: string | null
    /** ServiceNow does not hold the right Vault Sentinel ID yet: 'set' it, or 'clear' it. */
    snPending: 'set' | 'clear' | null
    lastSeenAt: string | null
}

export interface ServiceNowUpdates {
    mode: 'automatic' | 'manual'
    table: string
    field: string
    instanceUrl: string | null
    updates: Array<{ id: number; sysId: string; ciName: string | null; ciClass: string | null; action: 'set' | 'clear'; value: string | null }>
}

export interface MatchCandidate {
    objectId: string
    description: string | null
    objectService: string | null
    /** The CI that already has this object, if any. */
    matchedToCi: string | null
}

export interface RemovePreview {
    objectId: string
    dependencies: Array<{ scenario: number; dependent: string | null; dependsOn: string | null }>
}

export interface CMDBSyncStats {
    new: number
    changed: number
    deleted: number
    synced: number
    removed: number
    unmatched: number
    serviceNowPending: number
    total: number
}

export interface CMDBSyncLog {
    id: number
    syncType: string
    status: string
    totalCIs: number
    newItems: number
    changedItems: number
    deletedItems: number
    errorMessage: string | null
    startedAt: string
    completedAt: string | null
}

export interface CMDBRelationshipItem {
    id: number
    sysId: string
    parentSysId: string
    childSysId: string
    parentCiName: string | null
    childCiName: string | null
    parentObjectId: string | null
    childObjectId: string | null
    relationshipType: string | null
    relationshipTypeName: string | null
    syncStatus: 'new' | 'synced' | 'deleted' | 'ignored'
    lastSeenAt: string | null
    createdAt: string | null
}

export interface CMDBRelationshipStats {
    total: number
    new: number
    synced: number
    deleted: number
    ignored: number
}

export const servicenowCmdbApi = {
    getConfig: () =>
        api.get<{ success: boolean; data: ServiceNowConfig }>('/api/servicenow-cmdb/config'),

    saveConfig: (config: Partial<ServiceNowConfig>) =>
        api.put<{ success: boolean; data: { id: number; saved: boolean } }>('/api/servicenow-cmdb/config', config),

    triggerSync: () =>
        api.post<{ success: boolean; data: { syncLogId: number; status: string } }>('/api/servicenow-cmdb/sync'),

    getItems: (params?: { status?: string; page?: number; limit?: number }) =>
        api.get<{ success: boolean; data: CMDBSyncItem[]; page: number; perPage: number; total: number }>('/api/servicenow-cmdb/items', { params }),

    getStats: () =>
        api.get<{ success: boolean; data: CMDBSyncStats }>('/api/servicenow-cmdb/items/stats'),

    addItem: (id: number) =>
        api.post<{ success: boolean; data: { objectId: string; serviceNowUpdated: boolean; serviceNowPending: boolean; manual: boolean; serviceNowError: string | null } }>(`/api/servicenow-cmdb/items/${id}/add`),

    getServiceNowUpdates: () =>
        api.get<{ success: boolean; data: ServiceNowUpdates }>('/api/servicenow-cmdb/servicenow-updates'),

    searchObjects: (q: string) =>
        api.get<{ success: boolean; data: MatchCandidate[] }>('/api/servicenow-cmdb/objects-search', { params: { q } }),

    matchItem: (id: number, objectId: string) =>
        api.post<{ success: boolean; data: { matched: boolean; objectId: string } }>(`/api/servicenow-cmdb/items/${id}/match`, { objectId }),

    matchNewObject: (id: number, body: { objectId: string; description: string; objectService: 'Object' | 'Service'; ClassificationID: number }) =>
        api.post<{ success: boolean; data: { matched: boolean; created: boolean; objectId: string } }>(`/api/servicenow-cmdb/items/${id}/match-new`, body),

    unmatchItem: (id: number) =>
        api.post(`/api/servicenow-cmdb/items/${id}/unmatch`),

    getRemovePreview: (id: number) =>
        api.get<{ success: boolean; data: RemovePreview }>(`/api/servicenow-cmdb/items/${id}/remove-preview`),

    removeItem: (id: number) =>
        api.post<{ success: boolean; data: { objectId: string; dependenciesRemoved: number; serviceNowCleared: boolean; serviceNowPending: boolean; serviceNowError: string | null } }>(`/api/servicenow-cmdb/items/${id}/remove`),

    dismissItem: (id: number) =>
        api.post(`/api/servicenow-cmdb/items/${id}/dismiss`),

    dismissNeverAdded: () =>
        api.post<{ success: boolean; data: { dismissed: number } }>('/api/servicenow-cmdb/items/dismiss-never-added'),

    updateItem: (id: number) =>
        api.post<{ success: boolean; data: { objectId: string } }>(`/api/servicenow-cmdb/items/${id}/update`),

    ignoreItem: (id: number) =>
        api.post<{ success: boolean; data: { ignored: boolean } }>(`/api/servicenow-cmdb/items/${id}/ignore`),

    getSyncLog: () =>
        api.get<{ success: boolean; data: CMDBSyncLog[] }>('/api/servicenow-cmdb/sync-log'),

    getPendingCount: () =>
        api.get<{ success: boolean; data: { pendingCount: number } }>('/api/servicenow-cmdb/pending-count'),

    // Relationships
    syncRelationships: () =>
        api.post('/api/servicenow-cmdb/relationships/sync'),

    getRelationships: (params?: { status?: string; limit?: number; offset?: number }) =>
        api.get<{ success: boolean; data: { items: CMDBRelationshipItem[]; total: number } }>('/api/servicenow-cmdb/relationships', { params }),

    getRelationshipStats: () =>
        api.get<{ success: boolean; data: CMDBRelationshipStats }>('/api/servicenow-cmdb/relationships/stats'),

    addRelationship: (id: number, scenario?: number) =>
        api.post(`/api/servicenow-cmdb/relationships/${id}/add`, { scenario: scenario || 1 }),

    ignoreRelationship: (id: number) =>
        api.post(`/api/servicenow-cmdb/relationships/${id}/ignore`),

    deleteRelationshipMapping: (id: number, scenario?: number) =>
        api.delete(`/api/servicenow-cmdb/relationships/${id}/mapping`, { params: { scenario: scenario || 1 } }),

    // Incidents
    getIncidentContext: (objectId: string) =>
        api.get<{ success: boolean; data: IncidentContext }>('/api/servicenow-cmdb/incident-context', { params: { objectId } }),

    createIncident: (payload: IncidentPayload) =>
        api.post<{ success: boolean; data: IncidentResult }>('/api/servicenow-cmdb/incident', payload),
}

export interface IncidentContext {
    enabled: boolean
    instanceUrl: string
    ci: { sysId: string; ciName: string | null; ciClass: string | null } | null
    defaultCaller: string
    defaultAssignmentGroup: string
}

export interface IncidentPayload {
    short_description: string
    description?: string
    cmdb_ci?: string
    caller_id?: string
    urgency?: string
    impact?: string
    category?: string
    subcategory?: string
    assignment_group?: string
    contact_type?: string
    correlation_id?: string
    comments?: string
    work_notes?: string
}

export interface IncidentResult {
    number: string
    sysId: string
    link: string
}
